import { useState, useCallback } from "react";
import { useAccount } from "wagmi";
import {
  Copy,
  Download,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  Scissors,
  Wallet,
  ArrowRight,
  FileCode2,
  RefreshCcw,
  ShieldAlert,
} from "lucide-react";
import toast from "react-hot-toast";
import { cn } from "../lib/utils";

// ─── Constants ────────────────────────────────────────────────────────────────

const ALLOWED_ADDRESSES = [
  "0x49bbefa1d94702c0e9a5eaddec7c3c5d3eb9086b", // TREASURY
  "0x31b8f9a85fa9b9258b5b5f1875dbd863999dca76", // TOKEN_OWNER
];

const CHAIN_OPTIONS = [
  { value: "jasmychain", label: "JasmyChain (680)", color: "#F7931A" },
  { value: "pulsechain", label: "PulseChain (369)", color: "#9B59B6" },
] as const;

// ─── Types ────────────────────────────────────────────────────────────────────

interface RewriteEntry {
  from: string;
  to: string;
  label: string;
}

interface CloneResult {
  ok: true;
  contractName: string;
  compilerVersion: string;
  sourceCode: string;
  abi: string;
  constructorArgs: string;
  rewrites: RewriteEntry[];
  originalAddress: string;
  targetChain: string;
  clonedAt: string;
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────

function SkeletonBlock({ className }: { className?: string }) {
  return (
    <div
      className={cn("rounded-card animate-pulse", className)}
      style={{ background: "rgba(255,255,255,0.05)" }}
    />
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-fade-in">
      <SkeletonBlock className="h-10 w-64" />
      <SkeletonBlock className="h-6 w-48" />
      <div className="flex gap-2 flex-wrap">
        {[1, 2, 3].map((i) => (
          <SkeletonBlock key={i} className="h-7 w-40" />
        ))}
      </div>
      <SkeletonBlock className="h-96 w-full" />
    </div>
  );
}

// ─── Address chip ─────────────────────────────────────────────────────────────

function RewriteChip({ rewrite }: { rewrite: RewriteEntry }) {
  const shortFrom = `${rewrite.from.slice(0, 6)}…${rewrite.from.slice(-4)}`;
  const shortTo = `${rewrite.to.slice(0, 6)}…${rewrite.to.slice(-4)}`;

  return (
    <div
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-button text-xs font-mono border"
      style={{
        background: "rgba(247,147,26,0.08)",
        borderColor: "rgba(247,147,26,0.25)",
        color: "rgba(255,255,255,0.8)",
      }}
    >
      <span className="text-text-tertiary">{rewrite.label}</span>
      <span className="text-red-400">{shortFrom}</span>
      <ArrowRight className="w-3 h-3 text-text-tertiary flex-shrink-0" />
      <span className="text-jasmy">{shortTo}</span>
    </div>
  );
}

// ─── Copy button ──────────────────────────────────────────────────────────────

function CopyButton({
  text,
  label = "COPY",
  className,
}: {
  text: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-button text-xs font-semibold transition-all duration-200",
        copied
          ? "text-green-400 border border-green-400/30 bg-green-400/10"
          : "text-text-secondary border border-border hover:text-text-primary hover:border-border-bright",
        className
      )}
    >
      {copied ? (
        <CheckCircle2 className="w-3.5 h-3.5" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
      {copied ? "COPIED" : label}
    </button>
  );
}

// ─── Download button ──────────────────────────────────────────────────────────

function DownloadButton({
  content,
  filename,
}: {
  content: string;
  filename: string;
}) {
  const handleDownload = useCallback(() => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  }, [content, filename]);

  return (
    <button
      onClick={handleDownload}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-button text-xs font-semibold text-text-secondary border border-border hover:text-text-primary hover:border-border-bright transition-all duration-200"
    >
      <Download className="w-3.5 h-3.5" />
      DOWNLOAD .SOL
    </button>
  );
}

// ─── ABI Section ──────────────────────────────────────────────────────────────

function AbiSection({ abi }: { abi: string }) {
  const [open, setOpen] = useState(false);

  // Format JSON nicely
  let formatted = abi;
  try {
    formatted = JSON.stringify(JSON.parse(abi), null, 2);
  } catch {
    // use raw
  }

  return (
    <div
      className="rounded-card border overflow-hidden"
      style={{ borderColor: "rgba(255,255,255,0.08)" }}
    >
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
        onClick={() => setOpen((v) => !v)}
        style={{ background: "rgba(255,255,255,0.02)" }}
      >
        <div className="flex items-center gap-2">
          <FileCode2 className="w-4 h-4" />
          <span>ABI (Raw JSON)</span>
        </div>
        <div className="flex items-center gap-2">
          {open && <CopyButton text={formatted} label="COPY ABI" />}
          {open ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </button>

      {open && (
        <div
          className="border-t"
          style={{ borderColor: "rgba(255,255,255,0.06)" }}
        >
          <pre
            className="p-4 text-xs font-mono text-text-secondary overflow-auto max-h-64"
            style={{ background: "rgba(0,0,0,0.3)" }}
          >
            {formatted}
          </pre>
        </div>
      )}
    </div>
  );
}

// ─── Results Panel ────────────────────────────────────────────────────────────

function ResultsPanel({ result }: { result: CloneResult }) {
  const chainLabel =
    CHAIN_OPTIONS.find((c) => c.value === result.targetChain)?.label ??
    result.targetChain;

  return (
    <div className="space-y-5 animate-slide-up">
      {/* Contract header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-400" />
          <span className="text-lg font-bold text-text-primary">
            {result.contractName}
          </span>
        </div>
        <span
          className="px-2.5 py-0.5 rounded-button text-xs font-mono border"
          style={{
            background: "rgba(255,255,255,0.04)",
            borderColor: "rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.5)",
          }}
        >
          {result.compilerVersion}
        </span>
        <span
          className="px-2.5 py-0.5 rounded-button text-xs font-semibold"
          style={{
            background: "rgba(247,147,26,0.15)",
            color: "#F7931A",
          }}
        >
          {chainLabel}
        </span>
      </div>

      {/* Rewrites applied */}
      {result.rewrites.length > 0 ? (
        <div>
          <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-2">
            Rewrites Applied ({result.rewrites.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {result.rewrites.map((r, i) => (
              <RewriteChip key={i} rewrite={r} />
            ))}
          </div>
        </div>
      ) : (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-button text-sm"
          style={{
            background: "rgba(247,147,26,0.08)",
            border: "1px solid rgba(247,147,26,0.2)",
            color: "rgba(255,255,255,0.6)",
          }}
        >
          <AlertTriangle className="w-4 h-4 text-jasmy flex-shrink-0" />
          No known address patterns were detected. Verify all addresses manually.
        </div>
      )}

      {/* Warning notice */}
      <div
        className="flex items-start gap-3 px-4 py-3 rounded-card text-sm"
        style={{
          background: "rgba(239,68,68,0.07)",
          border: "1px solid rgba(239,68,68,0.2)",
        }}
      >
        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
        <p className="text-text-secondary leading-relaxed">
          Review all addresses carefully before deploying. The cloner replaces
          known patterns but custom addresses must be verified manually.
        </p>
      </div>

      {/* Source code */}
      <div
        className="rounded-card border overflow-hidden"
        style={{ borderColor: "rgba(255,255,255,0.08)" }}
      >
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{
            background: "rgba(255,255,255,0.02)",
            borderColor: "rgba(255,255,255,0.06)",
          }}
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-text-secondary">
            <FileCode2 className="w-4 h-4" />
            <span>{result.contractName}.sol</span>
            <span className="text-xs text-text-tertiary font-mono">
              {result.sourceCode.length.toLocaleString()} chars
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CopyButton text={result.sourceCode} label="COPY SOURCE" />
            <DownloadButton
              content={result.sourceCode}
              filename={`${result.contractName}.sol`}
            />
          </div>
        </div>

        <textarea
          readOnly
          value={result.sourceCode}
          className="w-full font-mono text-xs text-text-secondary resize-none focus:outline-none p-4"
          style={{
            background: "rgba(0,0,0,0.35)",
            minHeight: "480px",
            height: "480px",
            color: "rgba(255,255,255,0.75)",
            lineHeight: "1.6",
            tabSize: 2,
          }}
          spellCheck={false}
        />
      </div>

      {/* ABI */}
      <AbiSection abi={result.abi} />

      {/* Constructor args */}
      {result.constructorArgs && result.constructorArgs.length > 0 && (
        <div
          className="rounded-card border p-4 space-y-2"
          style={{
            borderColor: "rgba(255,255,255,0.08)",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
            Constructor Arguments (ABI-encoded)
          </p>
          <pre
            className="text-xs font-mono text-text-secondary break-all whitespace-pre-wrap"
          >
            {result.constructorArgs}
          </pre>
        </div>
      )}

      {/* Clone metadata */}
      <p className="text-xs text-text-tertiary text-right">
        Cloned {new Date(result.clonedAt).toLocaleString()} · Original:{" "}
        <span className="font-mono">{result.originalAddress}</span>
      </p>
    </div>
  );
}

// ─── Restricted gate ──────────────────────────────────────────────────────────

function RestrictedGate({ connected }: { connected: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
        style={{
          background: "rgba(239,68,68,0.1)",
          border: "1px solid rgba(239,68,68,0.3)",
        }}
      >
        <ShieldAlert className="w-8 h-8 text-red-400" />
      </div>
      <h2 className="text-2xl font-bold text-text-primary mb-3">
        RESTRICTED ACCESS
      </h2>
      {connected ? (
        <p className="text-text-secondary max-w-sm">
          Your connected wallet is not authorised to use the Token Cloner. This
          tool is reserved for internal use only.
        </p>
      ) : (
        <p className="text-text-secondary max-w-sm">
          Connect your wallet to verify access. This tool is restricted to
          authorised addresses only.
        </p>
      )}
      {!connected && (
        <p className="mt-4 text-xs text-text-tertiary">
          Use the Connect button in the header to connect.
        </p>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function TokenClonerPage() {
  const { address, isConnected } = useAccount();

  const [contractAddress, setContractAddress] = useState("");
  const [targetChain, setTargetChain] =
    useState<"jasmychain" | "pulsechain">("jasmychain");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CloneResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Wallet gate
  const isAllowed =
    isConnected &&
    address != null &&
    ALLOWED_ADDRESSES.includes(address.toLowerCase());

  const handleFetch = useCallback(async () => {
    setError(null);
    setResult(null);

    const trimmed = contractAddress.trim();
    if (!/^0x[0-9a-fA-F]{40}$/.test(trimmed)) {
      setError("Enter a valid Ethereum contract address (0x...)");
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        address: trimmed,
        chain: targetChain,
      });

      const res = await fetch(`/api/clone-token?${params.toString()}`);
      const data = await res.json();

      if (!data.ok) {
        throw new Error(data.error ?? "Unknown server error");
      }

      setResult(data as CloneResult);
      toast.success(`${data.contractName} cloned successfully`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Fetch failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [contractAddress, targetChain]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !loading) {
        void handleFetch();
      }
    },
    [handleFetch, loading]
  );

  const handleReset = useCallback(() => {
    setResult(null);
    setError(null);
    setContractAddress("");
  }, []);

  // ── Render: not connected or not allowed ─────────────────────────────────

  if (!isConnected || !isAllowed) {
    return (
      <div className="relative min-h-[calc(100vh-4rem)]">
        {/* BG orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-60 -right-60 w-[700px] h-[700px] rounded-full bg-red-500/[0.02] blur-[140px]" />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-primary/[0.03] blur-[120px]" />
        </div>
        <div className="relative flex flex-col items-center justify-start pt-10 pb-20 px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center gap-2 border rounded-full px-4 py-1.5 text-sm font-medium mb-4"
              style={{
                background: "rgba(247,147,26,0.08)",
                borderColor: "rgba(247,147,26,0.2)",
                color: "#F7931A",
              }}
            >
              <Scissors className="w-3.5 h-3.5" />
              Internal Tool
            </div>
            <h1 className="neon-text-orange font-display text-4xl sm:text-5xl font-bold mb-3 animate-neon-pulse">
              Token Cloner
            </h1>
            <p className="text-text-secondary text-base sm:text-lg max-w-lg mx-auto">
              Fetch any verified Ethereum contract and rewrite it for any chain
            </p>
          </div>
          <div className="w-full max-w-xl">
            <RestrictedGate connected={isConnected} />
          </div>
        </div>
      </div>
    );
  }

  // ── Render: authorised ────────────────────────────────────────────────────

  return (
    <div className="relative min-h-[calc(100vh-4rem)]">
      {/* BG orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-60 -right-60 w-[800px] h-[800px] rounded-full bg-[#F7931A]/[0.02] blur-[150px]" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-secondary/[0.03] blur-[120px]" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        {/* Page header */}
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center gap-2 border rounded-full px-4 py-1.5 text-sm font-medium mb-4"
            style={{
              background: "rgba(247,147,26,0.08)",
              borderColor: "rgba(247,147,26,0.2)",
              color: "#F7931A",
            }}
          >
            <Scissors className="w-3.5 h-3.5" />
            Internal Tool
          </div>
          <h1 className="neon-text-orange font-display text-4xl sm:text-5xl font-bold mb-3 animate-neon-pulse">
            Token Cloner
          </h1>
          <p className="text-text-secondary text-base sm:text-lg max-w-xl mx-auto">
            Fetch any verified Ethereum contract and rewrite it for any chain.
            Address substitutions applied automatically.
          </p>
        </div>

        {/* Auth badge */}
        <div className="flex justify-center mb-8">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-button text-xs border"
            style={{
              background: "rgba(57,255,20,0.07)",
              borderColor: "rgba(57,255,20,0.2)",
              color: "#39FF14",
            }}
          >
            <Wallet className="w-3.5 h-3.5" />
            <span className="font-mono">
              {address?.slice(0, 6)}…{address?.slice(-4)}
            </span>
            <span className="text-green-400 font-semibold">Authorised</span>
          </div>
        </div>

        {/* Input card */}
        <div
          className="glass-card p-6 mb-8"
          style={{ border: "1px solid rgba(247,147,26,0.2)" }}
        >
          <div className="space-y-4">
            {/* Contract address input */}
            <div>
              <label className="block text-sm font-semibold text-text-secondary mb-1.5">
                Ethereum Contract Address
              </label>
              <input
                type="text"
                value={contractAddress}
                onChange={(e) => {
                  setContractAddress(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={handleKeyDown}
                placeholder="0x..."
                className="input-field font-mono"
                disabled={loading}
                autoComplete="off"
                spellCheck={false}
              />
            </div>

            {/* Chain select */}
            <div>
              <label className="block text-sm font-semibold text-text-secondary mb-1.5">
                Target Chain
              </label>
              <div className="flex gap-2 flex-wrap">
                {CHAIN_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTargetChain(opt.value)}
                    disabled={loading}
                    className={cn(
                      "flex-1 sm:flex-none px-4 py-2.5 rounded-button text-sm font-semibold border transition-all duration-200 disabled:opacity-40",
                      targetChain === opt.value
                        ? "text-background"
                        : "text-text-secondary hover:text-text-primary"
                    )}
                    style={
                      targetChain === opt.value
                        ? {
                            background: opt.color,
                            borderColor: opt.color,
                            color: "#070816",
                            boxShadow: `0 0 14px ${opt.color}40`,
                          }
                        : {
                            background: "rgba(255,255,255,0.02)",
                            borderColor: "rgba(255,255,255,0.1)",
                          }
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions row */}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={() => void handleFetch()}
                disabled={loading || !contractAddress.trim()}
                className="btn-primary flex-1 sm:flex-none flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCcw className="w-4 h-4 animate-spin" />
                    Fetching…
                  </>
                ) : (
                  <>
                    <Scissors className="w-4 h-4" />
                    FETCH & REWRITE
                  </>
                )}
              </button>

              {(result || error) && (
                <button
                  onClick={handleReset}
                  disabled={loading}
                  className="btn-secondary flex items-center gap-2"
                >
                  <RefreshCcw className="w-4 h-4" />
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div
            className="flex items-start gap-3 px-4 py-4 rounded-card mb-8 animate-fade-in"
            style={{
              background: "rgba(239,68,68,0.07)",
              border: "1px solid rgba(239,68,68,0.25)",
            }}
          >
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-400 mb-0.5">
                Clone Failed
              </p>
              <p className="text-sm text-text-secondary">{error}</p>
            </div>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="animate-fade-in">
            <LoadingSkeleton />
          </div>
        )}

        {/* Results */}
        {!loading && result && <ResultsPanel result={result} />}
      </div>
    </div>
  );
}
